<script setup>
import { router } from "@inertiajs/vue3";
import ActionButton from "@js/components/ActionButton.vue";
import InputField from "@js/components/fields/InputField.vue";
import SpinnerIcon from "@js/components/icons/SpinnerIcon.vue";
import TabGroup from "@js/components/tabs/TabGroup.vue";
import TabPanel from "@js/components/tabs/TabPanel.vue";
import { goBack, useRoute } from "@js/utils/route";
import { Form } from "vee-validate";
import { reactive, ref } from "vue";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";
import { object, string } from "yup";

const { t } = useI18n();

const props = defineProps({
  tenant: {
    type: Object,
    required: true,
  },
});

const { route } = useRoute();

const form = reactive({
  url: "",
});

const validationSchema = object({
  url: string().required(t("error.required")),
});

const isSubmitting = ref(false);

const selectedTab = ref(0);

function changeTab(index) {
  selectedTab.value = index;
}

function submit() {
  let url = route("front.projectManagement.dev.postPageSpeedUrl", { tenant: props.tenant.id });

  let postData = {
    url: form.url,
  };

  let error;

  fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-TOKEN": document
        .querySelector('meta[name="csrf-token"]')
        .getAttribute("content"),
    },
    body: JSON.stringify(postData),
  })
    .then((response) => {
      error = !response.ok;

      return response.json();
    })
    .then((data) => {
      if (error) {
        toast.error(data.message, { position: "top-right" });
      } else {
        toast.success(t("toast.successfullySaved"), {
          position: "top-right",
        });

        goBack();
      }
    })
    .finally(() => {
      isSubmitting.value = false;
    })
    .catch((error) => {
      toast.error(error.message, { position: "top-right" });
      router.visit(
        route("front.projectManagement.dev.pageSpeed", { tenant: props.tenant.id }),
      );
    });
}

function deleteCallback() {
  toast.success(t("all.toast.successfullyDeleted"), { position: "top-right" });

  router.visit(route("front.projectManagement.dev.pageSpeed", { tenant: props.tenant.id }));
}
</script>

<template>
  <Form
    :validation-schema="validationSchema"
    @submit="submit"
    v-slot="{ errors }"
  >
    <TabGroup
      :selected-tab="selectedTab"
      :change-tab="changeTab"
      :tabs="{
        information: t('title.information'),
      }"
    >
      <template #panels>
        <TabPanel>
          <InputField
            v-model="form.url"
            :label="t('label.url')"
            field-name="url"
            field-id="url"
            type="url"
            required
          />
        </TabPanel>
      </template>
    </TabGroup>

    <div
      class="flex flex-wrap items-center gap-2 rounded-b border border-gray-200 bg-white p-6"
    >
      <ActionButton type="transparent" :callback="() => goBack()">
        {{ t("button.back") }}
      </ActionButton>

      <ActionButton
        submit
        :disabled="Object.keys(errors).length > 0 || isSubmitting"
      >
        <template v-if="!isSubmitting">
          {{ t("button.save") }}
        </template>

        <template v-else>
          <SpinnerIcon
            class="mr-1 size-4 animate-spin fill-white text-gray-200"
          />
          {{ t("button.saveInProgress") }}
        </template>
      </ActionButton>
    </div>
  </Form>
</template>
